import React, {useCallback} from 'react';

import {Button, Icon, Text} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import InvitedStudentsActionsDropdown from 'client/Classrooms/Classroom/Teacher/ManageRoster/InvitedStudents/InvitedStudentsActionsDropdown/InvitedStudentsActionsDropdown.react';
import {getInvitationsQuery} from 'client/Classrooms/Classroom/Teacher/People/people.queries';

import {InviteMenu} from '../InviteMenu';
import {ErrorRetryState} from '../../components/ErrorRetryState';
import {InviteMethodCards} from './InviteMethodCards';
import {PeopleEmptyState} from './PeopleEmptyState';
import {RosterTable} from './RosterTable';
import type {RosterColumn} from './RosterTable';
import {ROSTER_PAGE_SIZE, useRosterPagination} from './useRosterPagination';
import {useSortState} from './useSortState';
import type {SortDir} from './useSortState';
import type {RosterClassroom, RosterInvitation} from './usePeopleRoster';

// sent_email_at can be null from a race; fall back to updated_at (mirrors the legacy column).
const invitedDate = (invitation: RosterInvitation) =>
  invitation.getSentEmailAt() ?? invitation.getUpdatedAt() ?? null;

// Sortable columns → real Mandark sort fields (mirrors the legacy InvitedStudents table's sortBy).
const INVITATION_SORT_FIELD: Record<string, string> = {
  email: 'email',
  lastInvited: 'sent_email_at'
};

const toSortParam = (key: string, dir: SortDir): string =>
  `${dir === 'desc' ? '-' : ''}${INVITATION_SORT_FIELD[key] ?? 'sent_email_at'}`;

interface Props {
  classroom: RosterClassroom;
  search: string;
  onShowModal: (modal: string) => void;
}

/**
 * Pending invitations, server-paged (25/page, load-on-scroll). Email search and sort are Mandark query
 * params matching the legacy view, so they span all invitations rather than the loaded page.
 */
export const InvitedList = ({classroom, search, onShowModal}: Props) => {
  const classId = classroom.getId();
  const {sortKey, sortDir, toggleSort} = useSortState('lastInvited', 'desc');

  const buildQuery = useCallback(
    (page: number) => {
      const term = search.trim();
      return getInvitationsQuery(classId)
        .pageSize(ROSTER_PAGE_SIZE)
        .sort(toSortParam(sortKey, sortDir))
        .page(page)
        .filter({email: {case_insensitive_substring: encodeURIComponent(term)}}, !!term);
    },
    [classId, search, sortKey, sortDir]
  );

  const {rows, isLoading, isLoadingMore, hasNextPage, hasError, loadMore, refetch} =
    useRosterPagination<RosterInvitation>({buildQuery, label: 'invitations'});

  // Whole-roster empty states, read from the classroom count meta rather than a full fetch.
  if (classroom.getCountOfPendingClassroomInvitations() === 0) {
    if (classroom.getCountOfStudents() === 0) {
      return <InviteMethodCards onShowModal={onShowModal} />;
    }
    return (
      <PeopleEmptyState
        action={
          <InviteMenu
            classroom={classroom as unknown as ClassroomModelV1}
            classroomId={classroom.getId()}
            trigger={(triggerProps) => (
              <Button {...triggerProps} roundness='pill'>
                <Icon className='mr-x1' icon='user-plus' iconStyle='regular' />
                Invite Students
              </Button>
            )}
          />
        }
        body='All invitations were accepted.'
        icon='envelope'
        title='No invitations'
      />
    );
  }

  if (hasError) {
    return <ErrorRetryState copy='We couldn’t load this class’s invitations.' onRetry={refetch} />;
  }

  const columns: RosterColumn<RosterInvitation>[] = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half break-all',
      render: (invitation) => <Text>{invitation.getEmail()}</Text>
    },
    {
      key: 'lastInvited',
      label: 'Last invited',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half',
      render: (invitation) => <Text>{invitedDate(invitation)?.format('LL') ?? '—'}</Text>
    },
    {
      key: 'actions',
      headerClassName: 'w-x6',
      srOnlyHeader: 'Actions',
      cellClassName: 'px-x2 py-x1-half text-right',
      render: (invitation) => <InvitedStudentsActionsDropdown invitation={invitation} />
    }
  ];

  return (
    <RosterTable
      ariaLabel='Invited students'
      columns={columns}
      getRowKey={(invitation) => invitation.getId()}
      hasNextPage={hasNextPage}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      noResultsMessage='No invitations match your search.'
      onShowMore={loadMore}
      onSort={toggleSort}
      rows={rows}
      sortDir={sortDir}
      sortKey={sortKey}
    />
  );
};
